import ErrorMessage from '@/components/ErrorMessage';
import { BaseErrorModel, BaseResponseModel } from '@/models/core.model';
import { ClubTag, ClubTagTypeList, UpsertClubTag } from '@/models/club-tag.model';
import { getClubTag, upsertClubTag } from '@/services/club-tag.service';
import { AxiosError } from 'axios';
import React, { useEffect, useState } from 'react';
import { Drawer, DrawerProps, Button, Form, Schema, FormInstance, SelectPicker } from 'rsuite';
import { toastErrorMessage } from '@/utils/toasterMessage';

const modelSchema = Schema.Model<UpsertClubTag>({
  id: Schema.Types.NumberType(),
  type: Schema.Types.NumberType().isRequired('This field is required.'),
  key: Schema.Types.StringType()
    .isRequired('This field is required.')
    .maxLength(64, 'At most 64 characters.')
    .pattern(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'Use lowercase kebab-case, e.g. "long-run".'),
  title: Schema.Types.StringType()
    .isRequired('This field is required.')
    .maxLength(128, 'At most 128 characters.'),
});

const emptyModel: UpsertClubTag = {
  id: undefined,
  type: undefined,
  key: undefined,
  title: undefined,
};

interface ClubTagDrawerProps extends DrawerProps {
  clubTagId?: number,
};

const ClubTagDrawer = (props: ClubTagDrawerProps) => {
  const { clubTagId: id, open, onClose, ...rest } = props;
  const [formValue, setFormValue] = useState<UpsertClubTag>(emptyModel);
  const [formError, setFormError] = useState({});
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);

  useEffect(() => {
    if (id) {
      fetchData();
    } else {
      setFormValue(emptyModel);
    }
  }, [id]);

  const fetchData = async () => {
    if (id) {
      setLoading(true);
      try {
        const result: BaseResponseModel<ClubTag> = await getClubTag(id);
        if (result.success) {
          setFormValue(result.result);
        }
      } catch (err) {
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(`Error in fetching club tag:${errorModel?.errorMessage}`);
      }
      finally {
        setLoading(false);
      }
    }
  };

  const handleDrawerClose = (event?: React.SyntheticEvent<Element, Event> | undefined) => {
    setFormValue(emptyModel);
    setErrorModel(undefined);
    setLoading(false);
    if (onClose) {
      onClose(event);
    }
  };

  const handleSubmit = async event => {
    if (formRef.current && !formRef.current.check()) {
      console.log(formError);
      return;
    }
    setErrorModel(undefined);
    setLoading(true);
    try {
      const result: BaseResponseModel<ClubTag> = await upsertClubTag(formValue);
      if (result.success) {
        handleDrawerClose(event);
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    }
    finally {
      setLoading(false);
    }
  };

  const handleFormChange = (value: Record<string, any>) => {
    setFormValue(prevFormValue => ({
      ...prevFormValue,
      ...value
    }));
  };

  return (
    <Drawer backdrop="static" size="sm" placement="right"
      open={open}
      onClose={onClose}
      {...rest}
    >
      <Drawer.Header>
        <Drawer.Title>{id ? 'Edit a' : 'Add a new'} Club Tag</Drawer.Title>
        <Drawer.Actions>
          <Button onClick={handleSubmit} loading={loading} appearance="primary">
            Submit
          </Button>
          <Button onClick={handleDrawerClose} appearance="subtle">
            Cancel
          </Button>
        </Drawer.Actions>
      </Drawer.Header>

      <Drawer.Body>
        <Form name='upsert-club-tag' fluid ref={formRef}
          onChange={handleFormChange}
          onCheck={setFormError}
          formValue={formValue}
          model={modelSchema}>
          <Form.Group>
            <Form.Label>Type</Form.Label>
            <Form.Control name="type" accepter={SelectPicker} block
              data={ClubTagTypeList}
              searchable={false}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Key</Form.Label>
            <Form.Control name="key" value={formValue.key ?? ''} />
            <Form.HelpText>Stable identifier used by the app. Avoid changing it once in use.</Form.HelpText>
          </Form.Group>
          <Form.Group>
            <Form.Label>Title</Form.Label>
            <Form.Control name="title" value={formValue.title ?? ''} />
          </Form.Group>
        </Form>
        {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom: 20 }}></ErrorMessage>}
      </Drawer.Body>
    </Drawer>
  );
};

export default ClubTagDrawer;
